import { HTTPError } from "ky";

import { getClearStore, getStore } from "./storeUtils";

import { PUBLIC_AUTH_UI_ROUTES } from "./authRouteConstants";
import { tokenStorage } from "./tokenStorage";
import { extractErrorMessage } from "./errorUtils";

export const SESSION_EXPIRED_USER_MESSAGE = "Your session has expired. Please log in to continue.";

const LOGIN_PATH = "/auth/login";

// Flag to prevent multiple simultaneous logout attempts
let isLoggingOut = false;

export type TRouteLocationLike = {
  pathname: string;
  search?: Record<string, unknown> | string;
  hash?: string;
};

/**
 * Validates a post-login return path. Only same-origin relative paths are allowed.
 * Rejects protocol-relative URLs (open redirect via `//evil.com`).
 */
export function sanitizeReturnPath(path: string | null | undefined): string | null {
  if (!path || typeof path !== "string") {
    return null;
  }

  const trimmed = path.trim();
  if (!trimmed.startsWith("/") || trimmed.startsWith("//")) {
    return null;
  }

  if (PUBLIC_AUTH_UI_ROUTES.some((route) => trimmed === route || trimmed.startsWith(`${route}?`))) {
    return null;
  }

  return trimmed;
}

export function getCurrentReturnPath(): string {
  if (typeof window === "undefined") {
    return "/app";
  }

  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

export function getReturnPathFromLocation(location?: TRouteLocationLike): string | null {
  if (!location) {
    return sanitizeReturnPath(getCurrentReturnPath());
  }

  let search = "";
  if (typeof location.search === "string") {
    search = location.search.startsWith("?")
      ? location.search
      : location.search
        ? `?${location.search}`
        : "";
  } else if (location.search && typeof location.search === "object") {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(location.search)) {
      if (typeof value === "string" && value !== "") {
        params.set(key, value);
      } else if (typeof value === "number" || typeof value === "boolean") {
        params.set(key, String(value));
      }
    }
    const query = params.toString();
    if (query) {
      search = `?${query}`;
    }
  }

  const hash = location.hash
    ? location.hash.startsWith("#")
      ? location.hash
      : `#${location.hash}`
    : "";

  return sanitizeReturnPath(`${location.pathname}${search}${hash}`);
}

export function buildLoginRedirectUrl(returnPath?: string | null): string {
  const sanitized = sanitizeReturnPath(returnPath ?? getCurrentReturnPath());
  if (!sanitized) {
    return LOGIN_PATH;
  }

  const params = new URLSearchParams({ from: sanitized });
  return `${LOGIN_PATH}?${params.toString()}`;
}

export function getPostLoginRedirectPath(options: {
  from?: string | null;
  isAdmin?: boolean;
}): string {
  const sanitized = sanitizeReturnPath(options.from);
  if (sanitized) {
    return sanitized;
  }

  return options.isAdmin ? "/app/admin" : "/app";
}

export function isSessionExpiredHttpError(error: unknown): boolean {
  return error instanceof HTTPError && error.response?.status === 401;
}

export async function getSessionExpiredAwareErrorMessage(error: unknown, fallback: string): Promise<string> {
  if (isSessionExpiredHttpError(error)) {
    return SESSION_EXPIRED_USER_MESSAGE;
  }

  const message = await extractErrorMessage(error);
  return message || fallback;
}

/**
 * Performs the logout process: clears store, cookies, and redirects to login.
 * This is a shared function to avoid code duplication across the codebase.
 * Handles authentication failures from both HTTP and WebSocket sources.
 * @returns void
 */
export const performAuthFailureLogout = (): void => {
  // Prevent multiple simultaneous logout attempts
  if (isLoggingOut) {
    return;
  }

  // Don't redirect if already on a PUBLIC auth page (prevents redirect loops).
  // NOTE: Some `/auth/...` pages are part of authenticated flows (e.g. verification),
  // so we must still redirect to `/auth/login` from those when auth fails.
  const currentPath = window.location.pathname;
  if (PUBLIC_AUTH_UI_ROUTES.some((route) => currentPath.startsWith(route))) {
    return;
  }

  isLoggingOut = true;

  // Clear persisted store from sessionStorage first to prevent rehydration of stale tokens
  try {
    getClearStore()();
  } catch {
    // If clearStore is not available, continue with other cleanup
  }

  // Clear store and cookies synchronously before redirect
  // This ensures route guards see the cleared state
  try {
    getStore().getState().resetAuthStore();
  } catch {
    // If store is not available, continue with redirect
  }
  tokenStorage.clearAuthCookies();

  // Redirect immediately - the store is already cleared; preserve destination for post-login
  window.location.replace(buildLoginRedirectUrl(getCurrentReturnPath()));

  // Reset flag after redirect (though this may not execute if redirect is immediate)
  // The flag will be reset on next page load anyway
};
